import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Observable, Subscription } from 'rxjs';
import { from } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { Router, NavigationEnd, NavigationSkipped } from '@angular/router';
import { Hub } from 'aws-amplify/utils';
import { fetchAuthSession, signInWithRedirect, signOut } from 'aws-amplify/auth';
import { IUser } from '../../models/IUser';
import { UserActions } from 'src/app/store/user.actions';
import { Store } from '@ngxs/store';
import { post } from 'aws-amplify/api';
// const axios = require('axios').default;
import axios, { AxiosHeaders } from 'axios';
@Injectable({
    providedIn: 'root'
})
export class AuthenticationService implements OnDestroy {
    private login: boolean = false;
    private roles: Array<string> = [];
    private isLoggingOut = false;
    readonly idpProvider: string = environment.idpCognito;
    paramObsRoute: Subscription;
    private readonly initialUrl: string;

    constructor(private route: Router, private zone: NgZone, private store: Store) {
        this.initialUrl = window.location.pathname + window.location.search + window.location.hash;
        this.start();
        this.requestInterceptors();
        this.routeObs();
        if (
            window.location.href === `${environment.aws.oauth.redirectUrl}/` &&
            !window?.location?.href.split('code=')[1] &&
            !localStorage.hasOwnProperty(
                `CognitoIdentityServiceProvider.${environment.aws.userPoolWebClientId}.LastAuthUser`
            )
        ) {
            this.socialLogin(environment.idpCognito);
        }
        Hub.listen('auth', ({ payload }) => {
            switch (payload.event) {
                //  v6 successful sign-in
                case 'signedIn':
                case 'signInWithRedirect':
                    this.login = true;
                    this.handleUserRedirect();
                    break;
                //  v6 sign-out
                case 'signedOut':
                    this.login = false;
                    this.roles = [];
                    if (!this.isLoggingOut) {
                        this.route.navigate(['logout']);
                    }
                    break;
                // v6 failures
                case 'signInWithRedirect_failure':
                case 'tokenRefresh_failure':
                    // case 'signedOut_failure':
                    this.login = false;
                    this.roles = [];
                    this.route.navigate(['login']);
                    break;
                default:
                    break;
            }
        });
        this.handleUserRedirect();
    }
    public socialLogin(provider: string): void {
        signInWithRedirect({ provider: { custom: provider } })
            .catch(() => {
                this.signOut();
            });
    }
    handleUserAuth(): void {
        if (this.login) {
            this.handleUserRedirect();
        }
    }

    getUserRoles(): Array<String> {
        return this.roles;
    }
    getUserEmail(): Observable<string> {
        return from(
            (async () => {
                const session = await fetchAuthSession();
                if(!session.tokens){
                  this.route.navigate(['/login']);
                }
                const email = session.tokens?.idToken?.payload?.['email'] as string | undefined;
                return email ?? '';
            })()
        );
    }

    private setUserState(): void {
        this.getUser().pipe(take(1)).subscribe(user => {
            this.store.dispatch(new UserActions.SetUser(user));
        });
    }
    getUser(): Observable<IUser> {
        return this.getUserEmail().pipe(
            switchMap((email) => {
                const payload = { email };
                const op = post({
                    apiName: 'portal',
                    path: 'persons',
                    options: { body: payload as Record<string, any> },
                });
                return from(op.response.then(async (r) => (await r.body.json()) as unknown as IUser));
            })
        );
    }

    signOut(): void {
        if (this.isLoggingOut) return;
        this.isLoggingOut = true;

        localStorage.setItem('logout-event', Math.random().toString());

        signOut()
            .catch(() => {
                // Optional: handle/log the error while still ensuring Cognito logout is attempted.
                this.zone.run(() => {
                    this.isLoggingOut = false;
                    this.route.navigate(['/logout']);
                });
            })
            .finally(() => {
                this.redirectToCognitoLogout();
            });
    }

    private redirectToCognitoLogout(): void {
        const domain = environment.aws.oauth.domain;
        const clientId = environment.aws.userPoolWebClientId;
        const logoutUri = encodeURIComponent(environment.aws.oauth.redirectLogoutUrl);
        window.location.href = `https://${domain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;
    }
    private handleUserRedirect(): void {
        if (window.location.pathname === '/logout') return;

        fetchAuthSession()
            .then((session) => {
                // keep your existing store update
                this.setUserState();
                const payload = session.tokens?.idToken?.payload ?? {};
                const idpRole = payload['custom:Role'] as string | undefined;
                // Cognito groups can be string[] or string depending on token/TS
                const cognitoGroupsRaw = payload['cognito:groups'] as string[] | string | undefined;
                const cognitoGroups: string[] = Array.isArray(cognitoGroupsRaw)
                    ? cognitoGroupsRaw
                    : cognitoGroupsRaw
                        ? [cognitoGroupsRaw]
                        : [];
                if (idpRole) {
                    this.roles = [idpRole];
                } else if (cognitoGroups.length) {
                    this.roles = cognitoGroups;
                } else {
                    const stored = localStorage.getItem('role');
                    this.roles = stored ? [stored] : [];
                }
                const targetUrl = this.pickBestTargetUrl();
                this.zone.run(() => {
                    // avoiding re-navigation loops
                    if (targetUrl && this.route.url !== targetUrl) {
                        this.route.navigateByUrl(targetUrl);
                        return;
                    }
                    // if no targetUrl or already on it, ensure a sensible default only when needed
                    if (!targetUrl || this.route.url === '/' || this.route.url === '') {
                        this.route.navigateByUrl(this.defaultRouteByRole());
                    }
                });
            })
            .catch(() => {
                this.zone.run(() => this.route.navigate(['error/401']));
            });
    }

    private pickBestTargetUrl(): string | null {
        const routerUrl = this.route.url;
        const browserUrl = window.location.pathname + window.location.search + window.location.hash;
        const current = (routerUrl && routerUrl !== '/' && routerUrl !== '')
            ? routerUrl
            : (browserUrl && browserUrl !== '/' && browserUrl !== '')
                ? browserUrl
                : this.initialUrl;
        // blocklist routes you never want to "restore"
        if (this.isBlockedRestoreUrl(current)) {
            // fall back to previousUrl if exists
            const prev = window.sessionStorage.getItem('previousUrl');
            if (prev && !this.isBlockedRestoreUrl(prev)) return prev;
            return null;
        }
        // If user refreshed on a valid in-app route, stay there
        if (current && current !== '/' && current !== '') return current;
        const prev = window.sessionStorage.getItem('previousUrl');
        if (prev && !this.isBlockedRestoreUrl(prev)) return prev;
        return null;
    }
    private isBlockedRestoreUrl(url: string): boolean {
        if (!url) return true;
        // normalize
        const u = url.toLowerCase();
        // avoid restoring these
        if (u === '/logout' || u.startsWith('/error') || u === '/login') return true;
        // avoid redirect/callback pages (code=)
        if (u.includes('code=')) return true;
        return false;
    }
    private defaultRouteByRole(): string {
        // keep your original mappings
        if (this.roles.includes('participant')) return '/journey/home';
        if (this.roles.includes('facilitator')) return '/admin/programs-external';
        if (this.roles.includes('flightReport') || this.roles.includes('FlightReport')) return '/admin/reports/arrival';
        if (this.roles.includes('admin')) return '/admin';
        return '/error';
    }
    private requestInterceptors(): void {
        axios.interceptors.request.use(
            async (config) => {
                try {
                    const session = await fetchAuthSession();
                    const token = session.tokens?.accessToken?.toString();
                    if (!token) {
                        this.signOut();
                        return config;
                    }

                    config.headers = config.headers ?? new AxiosHeaders();
                    (config.headers as AxiosHeaders).set('Authorization', token);
                    return config;
                } catch (e) {
                    this.signOut();
                    return config;
                }
            },
            (error) => Promise.reject(error)
        );
    }

    // Sign Out all tabs/windows when logged out of any tab.
    public signIn = () => {
        this.login = true;
        this.route.navigate([''])
    }

    public logOut = () => {
        this.login = false;
        this.route.navigate(['logout']);
    }

    private start(): void {
        window.addEventListener("storage", this.storageEventListener.bind(this));
    }

    private storageEventListener(event: StorageEvent) {
        if (event.storageArea == localStorage) {
            if (event?.key && event.key == 'logout-event') {
                this.logOut()
            }
        }
    }

    private stop(): void {
        window.removeEventListener("storage", this.storageEventListener.bind(this));
    }
    /**
     * Keeps the last visited URL in sessionStorage so refresh restores it.
     * (Same logic as old)
     */
    public routeObs() {
        // prevent duplicate subscriptions if service gets reinstantiated somehow
        if (this.paramObsRoute) return;
        this.paramObsRoute = this.route.events.subscribe((event) => {
            if (
                (event instanceof NavigationEnd || event instanceof NavigationSkipped) &&
                event.url !== '/logout' &&
                !event.url.includes('/error') &&
                event.url !== '/login'
            ) {
                window.sessionStorage.setItem('previousUrl', event.url);
            }
        });
    }

    ngOnDestroy() {
        this.stop()
        this.paramObsRoute.unsubscribe();
    }
}
